import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  DollarSign, 
  CreditCard, 
  Scale, 
  Wrench, 
  Megaphone, 
  AlertTriangle, 
  Gavel,
  ChevronDown,
  ChevronUp,
  ArrowLeft
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AuthModal from '../components/AuthModal';
import SearchModal from '../components/SearchModal';

// Hero image
const HERO_IMAGE = "https://images.unsplash.com/photo-1604975701397-6365ccbd028a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHw0fHxjaW5lbWElMjB0aGVhdGVyJTIwY29udHJhY3QlMjBidXNpbmVzcyUyMGhhbmRzaGFrZSUyMGFncmVlbWVudHxlbnwwfHx8fDE3NzA3NDA4NTl8MA&ixlib=rb-4.1.0&q=85";

// Section images
const SECTION_IMAGES = {
  economic: "https://images.unsplash.com/photo-1612934622902-d3ad43c407b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxjaW5lbWElMjB0aGVhdGVyJTIwY29udHJhY3QlMjBidXNpbmVzcyUyMGhhbmRzaGFrZSUyMGFncmVlbWVudHxlbnwwfHx8fDE3NzA3NDA4NTl8MA&ixlib=rb-4.1.0&q=85",
  theater: "https://images.unsplash.com/photo-1615414015111-8d98cb65677e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxjaW5lbWElMjB0aGVhdGVyJTIwY29udHJhY3QlMjBidXNpbmVzcyUyMGhhbmRzaGFrZSUyMGFncmVlbWVudHxlbnwwfHx8fDE3NzA3NDA4NTl8MA&ixlib=rb-4.1.0&q=85"
};

// Contract sections data
const contractSections = [
  {
    id: 1,
    icon: FileText,
    title: "Objeto del Contrato",
    content: `La presente hoja regula las condiciones para la exhibición pública del contenido audiovisual referenciado, ya sea mediante retransmisión en directo o pase grabado (Encore/Diferido), en las salas del Exhibidor autorizadas por la Distribuidora.`
  },
  {
    id: 2,
    icon: DollarSign,
    title: "Modalidades Económicas",
    hasModalities: true,
    intro: "Se establece una de las dos siguientes modalidades de contratación, según se especifique en la carátula del contrato:",
    modalityA: {
      title: "MODALIDAD A: TANTO ALZADO (Flat Fee)",
      points: [
        { subtitle: "Canon Fijo", text: "El Exhibidor abonará a la Distribuidora una cantidad fija neta de [Importe] € por pase y sala." },
        { subtitle: "Independencia de Taquilla", text: "Dicho importe será exigible independientemente del número de asistentes o de la recaudación obtenida por el Exhibidor." },
        { subtitle: "Derechos de Exhibición", text: "El pago de este canon otorga el derecho de un único pase en el horario y fecha acordados." }
      ]
    },
    modalityB: {
      title: "MODALIDAD B: PORCENTAJE DE TAQUILLA (Box Office Split)",
      points: [
        { subtitle: "Reparto de Ingresos", text: "El Exhibidor y la Distribuidora se repartirán los ingresos brutos de taquilla (deducido el IVA) en una proporción del [X]% para la Distribuidora y [X]% para el Exhibidor." },
        { subtitle: "Mínimo Garantizado (Opcional)", text: "En caso de pactarse, el Exhibidor garantizará a la Distribuidora un mínimo de [Importe] € por sala. Si el porcentaje de taquilla resultante es inferior, se aplicará el Mínimo Garantizado." },
        { subtitle: "Control de Taquilla", text: "El Exhibidor se obliga a declarar de forma fidedigna el número de espectadores y la recaudación bruta a través de los sistemas oficiales (ComScore/Taquilla España)." }
      ]
    }
  },
  {
    id: 3,
    icon: CreditCard,
    title: "Forma de Liquidación y Pago",
    points: [
      { subtitle: "Reporte", text: "El Exhibidor deberá enviar el reporte de taquilla a la Distribuidora en un plazo máximo de 48 horas hábiles tras la finalización del evento." },
      { subtitle: "Facturación", text: "La Distribuidora emitirá la factura correspondiente una vez recibida la liquidación (en modalidad de porcentaje) o de forma inmediata (en modalidad de tanto alzado)." },
      { subtitle: "Plazo de Pago", text: "El pago se realizará mediante transferencia bancaria en un plazo máximo de 30 días desde la fecha de emisión de la factura, salvo pacto en contrario." },
      { subtitle: "Impuestos", text: "Todas las cantidades pactadas se entienden netas, debiendo repercutirse el IVA vigente en el momento de la facturación." }
    ]
  },
  {
    id: 4,
    icon: Scale,
    title: "Derechos de Autor y Canon de Comunicación Pública (SGAE)",
    content: `El Exhibidor será el único responsable del pago de los derechos de propiedad intelectual por comunicación pública ante las entidades de gestión correspondientes (SGAE/AIE/AISGE), salvo que se especifique por escrito que la Distribuidora asume dicho coste en eventos específicos.`
  },
  {
    id: 5,
    icon: Wrench,
    title: "Obligaciones Técnicas y Materiales",
    points: [
      { subtitle: "Suministro", text: "La Distribuidora facilitará el contenido mediante envío por Bravo." },
      { subtitle: "Pruebas", text: "El Exhibidor es responsable de realizar una prueba técnica (test de señal o de ingesta) al menos 48 horas antes del pase para garantizar la correcta ejecución del evento." }
    ]
  },
  {
    id: 6,
    icon: Megaphone,
    title: "Publicidad y Promoción",
    content: `El Exhibidor se compromete a promocionar el evento en sus canales oficiales (cartelería física, web, redes sociales y newsletters).

La Distribuidora suministrará los materiales promocionales básicos (póster digital, tráiler y kit de prensa). El Exhibidor no podrá alterar los materiales sin autorización previa.`
  },
  {
    id: 7,
    icon: AlertTriangle,
    title: "Cancelación y Fuerza Mayor",
    points: [
      { subtitle: "Cancelación por el Exhibidor", text: "Si el Exhibidor cancela el pase con menos de 7 días de antelación, deberá abonar el 50% del canon fijo o del mínimo garantizado." },
      { subtitle: "Fallo Técnico", text: "En caso de fallo en la recepción ajeno al Exhibidor, ambas partes acordarán una fecha de pase en diferido o la anulación de la factura, sin que haya lugar a indemnizaciones adicionales." }
    ]
  },
  {
    id: 8,
    icon: Gavel,
    title: "Jurisdicción",
    content: `Para cualquier controversia derivada del presente contrato, las partes se someten a la jurisdicción de los Juzgados y Tribunales de Valencia, con renuncia expresa a cualquier otro fuero que pudiera corresponderles.`
  }
];

// Accordion Section Component
const AccordionSection = ({ section, isOpen, onToggle }) => {
  const Icon = section.icon;
  
  return (
    <div 
      className={`border border-white/10 rounded-xl overflow-hidden transition-all duration-300 ${
        isOpen ? 'bg-white/5' : 'bg-transparent hover:bg-white/[0.02]'
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-4 p-5 text-left"
        data-testid={`contract-section-${section.id}`}
      >
        <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
          isOpen ? 'bg-[#e4002b]' : 'bg-white/10'
        }`}>
          <Icon className={`w-5 h-5 ${isOpen ? 'text-white' : 'text-white/70'}`} />
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className={`font-semibold text-lg transition-colors ${
            isOpen ? 'text-white' : 'text-white/80'
          }`}>
            {section.id}. {section.title}
          </h3>
        </div>
        
        <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
          isOpen ? 'bg-white/10' : 'bg-transparent'
        }`}>
          {isOpen ? (
            <ChevronUp className="w-5 h-5 text-white/70" />
          ) : (
            <ChevronDown className="w-5 h-5 text-white/50" />
          )}
        </div>
      </button>
      
      {isOpen && (
        <div className="px-5 pb-6 pt-0">
          <div className="pl-16">
            {/* Simple content */}
            {section.content && (
              <p className="text-white/70 leading-relaxed whitespace-pre-line">
                {section.content}
              </p>
            )}
            
            {/* Points list */}
            {section.points && (
              <ul className="space-y-4">
                {section.points.map((point, idx) => (
                  <li key={idx} className="flex gap-3">
                    <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#e4002b] mt-2.5" />
                    <div>
                      <span className="text-white font-medium">{point.subtitle}: </span>
                      <span className="text-white/70">{point.text}</span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            
            {/* Economic modalities */}
            {section.hasModalities && (
              <div className="space-y-6">
                <p className="text-white/70">{section.intro}</p>
                
                {/* Modality A */}
                <div className="bg-gradient-to-br from-[#e4002b]/10 to-transparent border border-[#e4002b]/20 rounded-xl p-5">
                  <h4 className="text-[#e4002b] font-semibold mb-4">{section.modalityA.title}</h4>
                  <ul className="space-y-3">
                    {section.modalityA.points.map((point, idx) => (
                      <li key={idx} className="flex gap-3">
                        <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#e4002b] mt-2.5" />
                        <div>
                          <span className="text-white font-medium">{point.subtitle}: </span>
                          <span className="text-white/70">{point.text}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Modality B */}
                <div className="bg-gradient-to-br from-amber-500/10 to-transparent border border-amber-500/20 rounded-xl p-5">
                  <h4 className="text-amber-400 font-semibold mb-4">{section.modalityB.title}</h4>
                  <ul className="space-y-3">
                    {section.modalityB.points.map((point, idx) => (
                      <li key={idx} className="flex gap-3">
                        <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-amber-500 mt-2.5" />
                        <div>
                          <span className="text-white font-medium">{point.subtitle}: </span>
                          <span className="text-white/70">{point.text}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ContractTermsPage = () => {
  const [authOpen, setAuthOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [openSections, setOpenSections] = useState([1, 2]); // First two open by default

  const toggleSection = (id) => {
    setOpenSections(prev => 
      prev.includes(id) 
        ? prev.filter(s => s !== id)
        : [...prev, id]
    );
  };

  const expandAll = () => setOpenSections(contractSections.map(s => s.id));
  const collapseAll = () => setOpenSections([]);

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="contract-terms-page">
      <Header 
        onOpenAuth={() => setAuthOpen(true)} 
        onOpenSearch={() => setSearchOpen(true)}
      />

      {/* Hero Section */}
      <div className="relative h-[50vh] min-h-[400px] overflow-hidden">
        <img 
          src={HERO_IMAGE}
          alt="Teatro"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/80 to-transparent" />
        
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 max-w-7xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6 transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>
          
          <div className="flex items-center gap-3 mb-4">
            <div className="w-1 h-12 bg-[#e4002b] rounded-full" />
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white">
              Condiciones de Contratación
            </h1>
          </div>
          
          <p className="text-white/60 text-lg md:text-xl max-w-2xl">
            Términos y condiciones para la exhibición de contenido audiovisual en salas de cine
          </p>
        </div>
      </div>

      {/* Introduction Banner */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <span className="inline-block px-3 py-1 bg-[#e4002b]/20 text-[#e4002b] text-sm rounded-full mb-4">
                Documento Legal
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Marco regulatorio para exhibidores
              </h2>
              <p className="text-white/60 leading-relaxed">
                Este documento establece las condiciones generales que regulan la relación comercial entre 
                la Distribuidora y el Exhibidor para la proyección de contenido audiovisual en salas de cine. 
                Incluye las modalidades económicas disponibles, obligaciones técnicas y condiciones de pago.
              </p>
            </div>
            
            <div className="relative aspect-video rounded-2xl overflow-hidden hidden md:block">
              <img 
                src={SECTION_IMAGES.theater}
                alt="Interior de teatro"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0a0a0a] to-transparent opacity-50" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation */}
      <div className="sticky top-0 z-30 bg-[#0a0a0a]/95 backdrop-blur-sm border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
          <div className="flex items-center justify-between gap-4">
            <span className="text-white/50 text-sm hidden sm:block">
              {openSections.length} de {contractSections.length} secciones expandidas
            </span>
            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={expandAll}
                className="px-4 py-2 text-sm text-white/70 hover:text-white border border-white/10 hover:border-white/30 rounded-lg transition-all"
                data-testid="expand-all-btn"
              >
                Expandir todo
              </button>
              <button
                onClick={collapseAll}
                className="px-4 py-2 text-sm text-white/70 hover:text-white border border-white/10 hover:border-white/30 rounded-lg transition-all"
                data-testid="collapse-all-btn"
              >
                Colapsar todo
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Contract Sections */}
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-12">
        <div className="space-y-3">
          {contractSections.map(section => (
            <AccordionSection
              key={section.id}
              section={section}
              isOpen={openSections.includes(section.id)}
              onToggle={() => toggleSection(section.id)}
            />
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-16">
        <h2 className="text-2xl font-bold text-white mb-8 text-center">
          Resumen de Modalidades
        </h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          {/* Flat Fee Card */}
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-[#e4002b]/20 to-[#e4002b]/5 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-2xl p-6 md:p-8 hover:border-[#e4002b]/30 transition-colors">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-14 h-14 bg-[#e4002b]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-7 h-7 text-[#e4002b]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-1">Tanto Alzado</h3>
                  <span className="text-[#e4002b] text-sm font-medium">Flat Fee</span>
                </div>
              </div>
              
              <ul className="space-y-3 text-white/70 text-sm">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#e4002b] mt-2 flex-shrink-0" />
                  Canon fijo por pase y sala
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#e4002b] mt-2 flex-shrink-0" />
                  Independiente de la asistencia
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#e4002b] mt-2 flex-shrink-0" />
                  Pago inmediato
                </li>
              </ul>
              
              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="text-white/50 text-xs">
                  Ideal para eventos con aforo limitado o cuando se busca certeza presupuestaria.
                </p>
              </div>
            </div>
          </div>

          {/* Box Office Split Card */}
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 to-amber-500/5 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-2xl p-6 md:p-8 hover:border-amber-500/30 transition-colors">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-14 h-14 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <CreditCard className="w-7 h-7 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-1">Porcentaje de Taquilla</h3>
                  <span className="text-amber-400 text-sm font-medium">Box Office Split</span>
                </div>
              </div>
              
              <ul className="space-y-3 text-white/70 text-sm">
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 flex-shrink-0" />
                  Reparto proporcional de ingresos
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 flex-shrink-0" />
                  Opción de mínimo garantizado
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 flex-shrink-0" />
                  Control vía ComScore/Taquilla España
                </li>
              </ul>
              
              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="text-white/50 text-xs">
                  Recomendado para eventos con gran potencial de audiencia y flexibilidad económica.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact CTA */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={SECTION_IMAGES.economic}
            alt="Cinema"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/90 to-[#0a0a0a]" />
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 md:px-6 py-16 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            ¿Tienes dudas sobre las condiciones?
          </h2>
          <p className="text-white/60 mb-8 max-w-xl mx-auto">
            Nuestro equipo está disponible para resolver cualquier consulta sobre las modalidades de contratación y ayudarte a elegir la mejor opción para tu cine.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/cinema/register"
              className="px-8 py-3 bg-[#e4002b] text-white font-semibold rounded-xl hover:bg-[#c70024] transition-colors"
              data-testid="register-cinema-cta"
            >
              Registrar mi cine
            </Link>
            <Link
              to="/cinema/login"
              className="px-8 py-3 border border-white/20 text-white font-semibold rounded-xl hover:bg-white/5 transition-colors"
              data-testid="cinema-login-cta"
            >
              Acceder al portal
            </Link>
          </div>
        </div>
      </div>

      <Footer />
      
      <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} />
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </div>
  );
};

export default ContractTermsPage;
